import { makeAutoObservable, runInAction } from 'mobx';
import { enableStaticRendering } from 'mobx-react-lite';
import router from 'next/router';
import { cloneDeep } from 'lodash';
import FilterMultiselectState from '@features/categories/mobx/state/FilterMultiselectState';
import FilterSelectState from '@features/categories/mobx/state/FilterSelectState';
import FilterInputState from '@features/categories/mobx/state/FilterInputState';
import FilterRangeState from '@features/categories/mobx/state/FilterRangeState';
import FilterBooleanState from '@features/categories/mobx/state/FilterBooleanState';
import FilterSortState from '@features/categories/mobx/state/FilterSortState';
import ProductState from '@features/categories/mobx/state/ProductState';
import ArrangementProductState from '@features/categories/mobx/state/ArrangementProductState';
import PaginationState from '@features/categories/mobx/state/PaginationState';
import { PRODUCT_OBJECT_SCOPE, PRODUCTS_RENDER_COUNTS, DEFAULT_THUMBNAIL_SIZES } from '@features/categories/const';
import logger from '@utils/logger';
import NextCategoriesApi from '@features/api/nextApiService/nextCategoriesApi';
import { IS_SHOP } from '@config/index';
import { clearBrowserSearchParams } from '@utils/replaceBrowserSearchParams';
import { IListingData, IArrangementProduct, IProductObject, IVirtualCategoryFilters } from '@features/categories/typescript';
const isServer = typeof window === 'undefined';
enableStaticRendering(isServer);
class FiltersStore {
  availableTypes = {
    multiselect: 'multiselect',
    // select: 'select',
    range: 'range',
    // input: 'input',
    boolean: 'boolean'
  };

  // NOTE: old implementation is commented out for now
  filters: Map<string, FilterMultiselectState
  // | FilterSelectState
  // | FilterInputState
  | FilterRangeState | FilterSortState | FilterBooleanState> = new Map();
  initialFilters: Map<string, FilterMultiselectState | FilterRangeState | FilterSortState | FilterBooleanState> = new Map();
  sorting: FilterSortState | null = null;
  initialSorting: {
    order: string | null;
    orderBy: string;
  } | null = null;
  products: ProductState[] = [];
  arrangementProducts: ArrangementProductState[] = [];
  pagination: PaginationState = {} as PaginationState;
  asideActive = false;
  allFiltersViewActive = false;
  activeFilterView = 'filters';
  backupData: IListingData | null = null;
  initAddress: string | null = null;
  categoryId: number | null = null;
  previousCategoryId: number | null = null;
  loadPreviousActive = false;
  fetchTrigger = 0;

  /** Avoids re-fetch when createFilters recreates the same logical query after populate. */
  lastFetchedFilterValues: string | null = null;
  lastFetchedSortingValue: string | null = null;
  pendingResetPagination = false;
  virtualCategoryFilters: IVirtualCategoryFilters | null = null;
  userActivity = false;
  userActivityInFilters: Record<string, boolean> = {};
  virtualCategorySlug: string | null = null;
  hashPageQuery: boolean | null = null;
  constructor(data, arrangementProducts, sortingData, categoryId: number, initAddress?: string, virtualCategoryFilters: IVirtualCategoryFilters | null = null, virtualCategorySlug: string | null = null) {
    makeAutoObservable(this);
    runInAction(() => {
      this.virtualCategorySlug = virtualCategorySlug;
      if (virtualCategoryFilters && !this.virtualCategoryFilters) {
        this.virtualCategoryFilters = virtualCategoryFilters;
      }
      this.createPagination();
      this.populateStoreWithData(data, sortingData, categoryId, initAddress, true);
      if (arrangementProducts) {
        this.setArrangementProducts(arrangementProducts);
      }

      // Dodaj nasłuchiwanie zmian w URL
      if (typeof window !== 'undefined') {
        if (this.hashPageQuery == null) {
          this.hashPageQuery = !!router?.query?.page;
        }
        window.addEventListener('popstate', this.handleUrlChange);
        router.events.on('routeChangeComplete', this.handleUrlChange);
      }
    });
  }
  toggleAside = () => {
    this.asideActive = !this.asideActive;
  };
  setAllFiltersViewActive = (value: boolean) => {
    this.allFiltersViewActive = value;
  };
  setActiveFilterView = (value: string) => {
    this.activeFilterView = value;
  };
  createSorting = (sorting, name, isInitial = false) => {
    this.sorting = new FilterSortState(sorting, name);
    if (typeof window !== 'undefined') this.sorting.setValueFromURLString();
    if (isInitial) {
      this.initialSorting = {
        order: sorting.order || null,
        orderBy: sorting.current
      };
    }
  };
  addProduct = (product, isPrevious = false) => {
    if (isPrevious) {
      this.products.unshift(new ProductState(product));
    } else {
      this.products.push(new ProductState(product));
    }
  };
  clearFilters = () => {
    for (const [filterName] of this.filters) {
      this.filters.delete(filterName);
    }
  };
  clearVirtualCategoryFilters = () => {
    this.userActivityInFilters = {};
    this.virtualCategoryFilters = null;
    this.virtualCategorySlug = null;
    this.clearFilters();
  };
  clearProducts = () => {
    this.products = [];
  };
  filterFactory = filter => {
    if (IS_SHOP !== 'true') {
      return null;
    }
    // NOTE: old implementation is commented out for now
    switch (filter.type) {
      case this.availableTypes.multiselect:
        return filter.options ? new FilterMultiselectState(filter) : null;
      // case this.availableTypes.select:
      //   return filter.data ? new FilterSelectState(filter) : null;
      // case this.availableTypes.input:
      //   return new FilterInputState(filter);
      case this.availableTypes.range:
        return new FilterRangeState(filter);
      case this.availableTypes.boolean:
        return filter.options ? new FilterBooleanState(filter) : null;
      // default:
      //   return new FilterInputState(filter);
      default:
        return null;
    }
  };
  resetFiltersAndSortURL = () => {
    clearBrowserSearchParams();
  };
  createFilters = (filtersData, isInitial = false) => {
    this.clearFilters();
    const map = new Map();
    filtersData.forEach(singleFilter => {
      const filterInstance = this.filterFactory(singleFilter);
      if (filterInstance && filterInstance instanceof FilterMultiselectState && Object.values(filterInstance.options).length === 0) return;
      if (filterInstance) map.set(singleFilter.name, filterInstance);
      if (typeof window !== 'undefined') filterInstance?.setValueFromURLString();
    });
    this.filters = map;
    if (isInitial) {
      const deepCopyMap = new Map();
      filtersData.forEach(singleFilter => {
        const filterInstance = this.filterFactory(singleFilter);
        if (filterInstance && filterInstance instanceof FilterMultiselectState && Object.values(filterInstance.options).length === 0) return;
        if (filterInstance) deepCopyMap.set(singleFilter.name, filterInstance);
      });
      this.initialFilters = deepCopyMap;
    }
  };
  addMoreProducts = productsData => {
    productsData.forEach(product => {
      this.addProduct(product);
    });
  };
  addPreviousProducts = productsData => {
    const previousProducts = productsData.reverse();
    previousProducts.forEach(product => {
      this.addProduct(product, true);
    });
  };
  createProducts = productsData => {
    this.clearProducts();
    this.addMoreProducts(productsData);
  };
  addArrangementProduct = (arrangementProducts: ArrangementProductState[]) => {
    this.arrangementProducts = arrangementProducts;
  };
  setArrangementProducts = (arrangementProductsData: IArrangementProduct[]) => {
    if (arrangementProductsData) {
      const arrangement: ArrangementProductState[] = [];
      arrangementProductsData.forEach((arrangementProduct: IArrangementProduct) => {
        if (arrangementProduct && (arrangementProduct.video.desktop || arrangementProduct.image.desktop)) {
          arrangement.push(new ArrangementProductState(arrangementProduct));
        }
      });
      this.addArrangementProduct(arrangement);
    }
  };
  createPagination = () => {
    this.pagination = new PaginationState({
      count: 0,
      productsToRender: 0,
      offset: 0
    });
  };
  resetFilters = () => {
    for (const [_, filter] of this.filters) {
      filter.resetValue(true);
    }
    this.userActivity = true;
    this.virtualCategoryFilters = {};
    this.sorting?.resetValue(true);
    this.resetFiltersAndSortURL();
  };
  setVirtualCategoryFilters = () => {
    if (this.virtualCategoryFilters) {
      for (const [_, filter] of this.filters) {
        const value = this.virtualCategoryFilters[filter.key];
        if (value && !filter.userActivity && !this.userActivityInFilters[filter.key]) {
          if (filter instanceof FilterBooleanState) filter.setValue(typeof value === 'string' ? [value] : value, true);
          if (filter instanceof FilterMultiselectState) filter.setValue(typeof value === 'string' ? [value] : value, true);
          if (filter instanceof FilterInputState || filter instanceof FilterSelectState) filter.setValue(typeof value === 'string' ? value : value[0], true);
          if (filter instanceof FilterRangeState && typeof value === 'string' && value.split(',').length === 2) {
            filter.setValue({
              min: +value.split(',')[0],
              max: +value.split(',')[1]
            }, true);
          }
        } else if (filter.userActivity) {
          delete this.virtualCategoryFilters[filter.key];
        }
      }
    }
  };
  get sortingQueryData() {
    return {
      order: this.sorting?.order,
      orderBy: this.sorting?.orderBy
    };
  }
  handleUserActivityInFilters = (): boolean => {
    let result = false;
    for (const [_, filter] of this.filters) {
      const {
        userActivity,
        key
      } = filter;
      if (userActivity) {
        this.userActivityInFilters[key] = true;
        result = true;
      }
    }
    return result;
  };
  getFiltersQueryData = () => {
    const filtersObject = {};
    for (const [_, filter] of this.filters) {
      const {
        key
      } = filter;
      if (filter instanceof FilterRangeState) {
        const {
          defaultValue,
          value
        } = filter;
        const {
          min,
          max
        } = value;
        if (min !== defaultValue.min || max !== defaultValue.max) {
          filtersObject[key] = value;
        }
      }
      if (filter instanceof FilterInputState || filter instanceof FilterSelectState) {
        const {
          value
        } = filter;
        if (value) filtersObject[key] = value;
      }
      if (filter instanceof FilterMultiselectState) {
        const {
          value
        } = filter;
        if (value.length) filtersObject[key] = value;
      }
      if (filter instanceof FilterBooleanState) {
        const {
          value
        } = filter;
        if (value.length) filtersObject[key] = value;
      }
    }
    return filtersObject;
  };
  fetchData = async categoryId => {
    try {
      // Ensure pagination is initialized before using it
      if (!this.pagination || typeof this.pagination.setOffset !== 'function') {
        this.createPagination();
      }
      const offset = this.pagination.page === 1 ? 0 : (this.pagination.page - 1) * PRODUCTS_RENDER_COUNTS;
      const data = await NextCategoriesApi.getMarketingCategoryProducts(categoryId, {
        scope: PRODUCT_OBJECT_SCOPE.join(','),
        limit: PRODUCTS_RENDER_COUNTS,
        offset,
        filters: this.getFiltersQueryData(),
        thumbnails: DEFAULT_THUMBNAIL_SIZES,
        ...this.sortingQueryData
      });
      if ('products' in data && 'filters' in data && 'pagination' in data) {
        return data;
      }
      logger.error('Fetching filters data failed (invalid object):', data);
      return this.backupData;
    } catch (error) {
      logger.error('Fetch data failed:', error);
      return this.backupData;
    }
  };
  fetchNextData = async categoryId => {
    try {
      // Ensure pagination is initialized before using it
      if (!this.pagination || typeof this.pagination.setOffset !== 'function') {
        this.createPagination();
      }
      const data = await NextCategoriesApi.getMarketingCategoryProducts(categoryId, {
        scope: PRODUCT_OBJECT_SCOPE.join(','),
        limit: PRODUCTS_RENDER_COUNTS,
        offset: this.pagination.offset + this.pagination.productsToRender,
        filters: this.getFiltersQueryData(),
        thumbnails: DEFAULT_THUMBNAIL_SIZES,
        ...this.sortingQueryData
      });
      if ('products' in data && 'filters' in data && 'pagination' in data) {
        return data;
      }
      logger.error('Fetching filters data failed (invalid object):', data);
      return this.backupData;
    } catch (error) {
      logger.error('Fetch data failed:', error);
      return this.backupData;
    }
  };
  fetchPreviousData = async categoryId => {
    try {
      // Ensure pagination is initialized before using it
      if (!this.pagination || typeof this.pagination.setOffset !== 'function') {
        this.createPagination();
      }
      // offset is already set to the previous page start by the caller (handleShowPreviousButton)
      const data = await NextCategoriesApi.getMarketingCategoryProducts(categoryId, {
        scope: PRODUCT_OBJECT_SCOPE.join(','),
        limit: PRODUCTS_RENDER_COUNTS,
        offset: this.pagination.offset,
        filters: this.getFiltersQueryData(),
        thumbnails: DEFAULT_THUMBNAIL_SIZES,
        ...this.sortingQueryData
      });
      if ('products' in data && 'filters' in data && 'pagination' in data) {
        return data;
      }
      logger.error('Fetching filters data failed (invalid object):', data);
      return this.backupData;
    } catch (error) {
      logger.error('Fetch data failed:', error);
      return this.backupData;
    }
  };
  populateStoreWithData = (data, sortingTrans, categoryId: number, initAddress?: string, isInitial = false) => {
    if (!data) return;
    this.handleUserActivityInFilters();
    this.backupData = data;
    this.categoryId = categoryId;
    const {
      products: newProducts,
      pagination: newPagination,
      filters: newFilters,
      sorting: newSorting
    } = data;
    this.createProducts(newProducts);
    this.createFilters(newFilters, isInitial);
    this.createSorting(newSorting, sortingTrans, isInitial);
    // Ensure pagination is initialized before using it
    if (!this.pagination || typeof this.pagination.setOffset !== 'function') {
      this.createPagination();
    }
    this.pagination.setOffset(newPagination.offset);
    this.pagination.setCount(newPagination.count);
    this.pagination.setProductsToRender(newPagination.limit);
    this.initAddress = initAddress || null;
    if (!this.userActivity) {
      this.setVirtualCategoryFilters();
    } else {
      this.virtualCategoryFilters = {};
    }
    this.syncLastFetchedQuerySnapshots();
  };
  fetchAndPopulateStoreWithData = async (categoryId, sortingTrans, options?: {
    initAddress?: string;
    resetPagination?: boolean;
  }) => {
    const scrollY = typeof window !== 'undefined' ? window.scrollY : null;
    if (options?.resetPagination) {
      if (this.pagination && typeof this.pagination.resetToFirstPage === 'function') {
        this.pagination.resetToFirstPage();
      } else {
        this.createPagination();
      }
    }
    const data = await this.fetchData(categoryId);
    if (data) {
      runInAction(() => {
        this.populateStoreWithData(data, sortingTrans, categoryId, options?.initAddress);
        this.pendingResetPagination = false;
      });
      if (options?.resetPagination && !this.virtualCategoryFilters && !this.userActivity) {
        this.pagination.setPageToURLString();
      }

      // Keep viewport stable after filter/sort refetch (product list height / router can move scroll)
      if (options?.resetPagination && scrollY !== null) {
        const restoreScroll = () => window.scrollTo({
          top: scrollY,
          behavior: 'instant'
        });
        requestAnimationFrame(restoreScroll);
        setTimeout(restoreScroll, 0);
      }
    }
  };
  loadMore = async (categoryId, page?: number) => {
    // Ensure pagination is initialized before using it
    if (!this.pagination || typeof this.pagination.setOffset !== 'function') {
      this.createPagination();
    }
    const data = await this.fetchNextData(categoryId);
    if (data) {
      runInAction(() => {
        const {
          products: newProducts
        } = data;
        this.addMoreProducts(newProducts);
        this.pagination.setProductsToRender(this.pagination.productsToRender + PRODUCTS_RENDER_COUNTS, page);
      });
      this.pagination.setPageToURLString();
    }
  };
  loadPrevious = async (categoryId, page?: number) => {
    // Ensure pagination is initialized before using it
    if (!this.pagination || typeof this.pagination.setOffset !== 'function') {
      this.createPagination();
    }
    const data = await this.fetchPreviousData(categoryId);
    if (data) {
      runInAction(() => {
        const {
          products: newProducts
        } = data;
        this.addPreviousProducts(newProducts);
        this.pagination.setProductsToRender(this.pagination.productsToRender + PRODUCTS_RENDER_COUNTS, page);
      });
      this.pagination.setPageToURLString();
    }
  };
  updateAddress = address => {
    this.initAddress = address;
  };
  get filterKeys() {
    return [...this.filters.keys()];
  }
  get filterValues() {
    if (Object.keys(this.getFiltersQueryData()).length === 0) {
      return null;
    }
    return JSON.stringify(this.getFiltersQueryData());
  }
  get sortingValue() {
    if (this.sortingQueryData && this.sortingQueryData.orderBy === '' && this.sortingQueryData.order === null) {
      return null;
    }
    return JSON.stringify(this.sortingQueryData);
  }
  fetchArrangementProductProducts = async (arrangementProductId: number) => {
    try {
      const data = await NextCategoriesApi.getArrangementProductProducts(arrangementProductId);
      return data;
    } catch (error) {
      logger.error('Fetch data failed:', error);
      return [];
    }
  };
  getArrangementProductProducts = async (arrangementProductId: number): Promise<IProductObject[]> => {
    if (this.arrangementProducts.length) {
      const arrangementProductIndex = this.arrangementProducts.findIndex(aP => aP.id === arrangementProductId);
      if (arrangementProductIndex === -1) {
        return [] as IProductObject[];
      }
      if (this.arrangementProducts[arrangementProductIndex]?.products.length) {
        return this.arrangementProducts[arrangementProductIndex]?.products;
      }
      const products = await this.fetchArrangementProductProducts(arrangementProductId);
      runInAction(() => {
        this.arrangementProducts[arrangementProductIndex].products = products;
      });
      return products;
    }
    return [] as IProductObject[];
  };
  areFiltersSameAsInitial = (): boolean => {
    const currentFilters = Object.fromEntries(Array.from(this.filters.entries()).map(([key, filter]) => [key, filter.value]));
    const initialFilters = Object.fromEntries(Array.from(this.initialFilters.entries()).map(([key, filter]) => [key, filter.value]));
    return JSON.stringify(currentFilters) === JSON.stringify(initialFilters);
  };
  isSortingSameAsInitial = (): boolean => {
    const currentSorting = this.sorting ? {
      order: this.sorting.order ?? 'asc',
      orderBy: this.sorting.orderBy || 'default'
    } : null;
    const initialSorting = this.initialSorting ? {
      order: this.initialSorting.order,
      orderBy: this.initialSorting.orderBy
    } : null;
    return JSON.stringify(currentSorting).toLowerCase() === JSON.stringify(initialSorting).toLowerCase();
  };
  setCurrentFiltersAsInitial = () => {
    this.initialFilters = cloneDeep(this.filters);
  };
  setCurrentSortingAsInitial = () => {
    this.initialSorting = {
      order: this.sorting?.order || 'asc',
      orderBy: this.sorting?.orderBy || 'default'
    };
  };
  setFetchTrigger = (value: number) => {
    this.fetchTrigger = value;
  };
  incrementFetchTrigger = (resetPagination = false) => {
    this.pendingResetPagination = resetPagination;
    this.fetchTrigger += 1;
  };
  syncLastFetchedQuerySnapshots = () => {
    this.lastFetchedFilterValues = this.filterValues;
    this.lastFetchedSortingValue = this.sortingValue;
  };
  updateCategoryId = (newCategoryId: number) => {
    if (this.categoryId !== newCategoryId) {
      this.previousCategoryId = this.categoryId;
      this.categoryId = newCategoryId;
    }
  };

  /**
   * Detach URL listeners when leaving the listing (e.g. product page).
   * Keeps categoryId and products so browser back can restore pagination state.
   */
  invalidateStore = () => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('popstate', this.handleUrlChange);
      router.events.off('routeChangeComplete', this.handleUrlChange);
    }
  };
  reattachListeners = () => {
    if (typeof window === 'undefined') return;
    window.removeEventListener('popstate', this.handleUrlChange);
    router.events.off('routeChangeComplete', this.handleUrlChange);
    window.addEventListener('popstate', this.handleUrlChange);
    router.events.on('routeChangeComplete', this.handleUrlChange);
  };
  resetStore = () => {
    runInAction(() => {
      // Wyczyść wszystkie dane
      this.clearProducts();
      this.clearFilters();
      this.arrangementProducts = [];
      this.createPagination();
      this.asideActive = false;
      this.allFiltersViewActive = false;
      this.activeFilterView = 'filters';
      this.backupData = null;
      this.initAddress = null;
      this.categoryId = null;
      this.previousCategoryId = null;
      this.loadPreviousActive = false;
      this.fetchTrigger = 0;
      this.lastFetchedFilterValues = null;
      this.lastFetchedSortingValue = null;
      this.pendingResetPagination = false;
      this.virtualCategoryFilters = null;
      this.userActivity = false;
      this.userActivityInFilters = {};
      this.virtualCategorySlug = null;
      this.sorting = null;
      this.initialSorting = null;
      this.initialFilters = new Map();
    });
  };
  handleUrlChange = () => {
    runInAction(() => {
      this.filters.forEach(filter => {
        filter.setValueFromURLString();
      });
      if (this.sorting) {
        this.sorting.setValueFromURLString();
      }
    });
    const currentPath = window.location.pathname;
    const pathSegments = currentPath.split('/').filter(Boolean);
    if (this.categoryId && pathSegments.length >= 1 && this.initAddress && this.initAddress.split('?')[0] !== currentPath.split('?')[0]) {
      this.fetchAndPopulateStoreWithData(this.categoryId, this.sorting?.name || '');
    }
  };
}
export default FiltersStore;